import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { InboxItem, Server } from '../api/types.js';
import { useAuth } from '../auth/index.js';
import { samePageOn, useCurrentServer } from '../hooks/use-current-server.js';
import { useInbox, useInboxes } from '../hooks/use-inbox.js';
import { useMail } from '../hooks/use-mail.js';
import { useMeetings } from '../hooks/use-meetings.js';
import { useSlack } from '../hooks/use-slack.js';
import { useSprint } from '../hooks/use-sprint.js';
import { useReviews } from '../hooks/use-reviews.js';
import { useDeployments } from '../hooks/use-deployments.js';
import { useTickets } from '../hooks/use-tickets.js';
import { WAITING, todoOf, waitingCounts, type WaitingKind } from '../utils/waiting.js';

// The menu (operator, 30/10/2026): the server shown, chosen in a list, with what waits on you there by kind, each a
// way into To do on those decisions, then its pages; below, what is not one server's. The sidebar and the phone's
// header show the same one.

const ICONS: Record<string, string[]> = {
  slack: ['M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z'],
  reviews: ['M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z', 'M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'],
  deploys: ['M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z'],
  mail: ['M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75'],
  tickets: ['M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z'],
  sessions: ['m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z'],
  other: ['M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z'],
  people: ['M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z'],
  sprint: ['M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5'],
  meetings: ['M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5'],
  files: ['M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z'],
  'server-settings': ['M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75'],
  todo: ['M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75l.75.75 1.5-1.5M3.75 12l.75.75 1.5-1.5m-2.25 6l.75.75 1.5-1.5'],
  servers: ['M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-13.5 0a3 3 0 0 1-3-3m3 3h13.5m-13.5-6a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0-6h13.5a3 3 0 1 0 0 6m-13.5 0h13.5m0-6a3 3 0 0 1 3 3m-3-3a3 3 0 0 0-3 3m3-3h-13.5m13.5 6a3 3 0 0 1 3-3'],
  schedules: ['M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'],
  costs: ['M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z'],
  settings: ['M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z', 'M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'],
};

function Icon({ name }: { name: string }) {
  return (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      {(ICONS[name] ?? []).map((d) => <path key={d} strokeLinecap="round" strokeLinejoin="round" d={d} />)}
    </svg>
  );
}

const STATUS_TONE: Record<string, string> = { running: 'var(--color-success)', stopped: 'var(--color-idle)' };
const toneOf = (status: string) => STATUS_TONE[status] ?? 'var(--color-warn)';
/** As its page names it: the organisation, not the instance */
const nameOf = (s: Pick<Server, 'name' | 'organization'>) => s.organization || s.name;

/** The count of a line: brand when something waits, warn when one of them is urgent; nothing when none */
function Count({ count, urgent, collapsed }: { count: number; urgent?: boolean; collapsed: boolean }) {
  if (!count) return null;
  return (
    <span className={collapsed ? 'menu-count menu-count-dot' : 'menu-count'} style={{ color: urgent ? 'var(--color-warn)' : 'var(--color-brand-light)' }}>
      {count}
    </span>
  );
}

interface Entry { to: string; label: string; icon: string; active: boolean; count?: number; urgent?: boolean; hint?: string }

function Line({ entry, collapsed, onNavigate }: { entry: Entry; collapsed: boolean; onNavigate?: () => void }) {
  const said = entry.count ? `${entry.label}, ${entry.count} waiting${entry.urgent ? ', urgent' : ''}` : entry.label;
  return (
    <Link to={entry.to} onClick={onNavigate} aria-current={entry.active ? 'page' : undefined} aria-label={collapsed || entry.count ? said : undefined}
      className={`sidebar-link ${entry.active ? 'sidebar-link-active' : ''}`} style={{ textDecoration: 'none' }}
      title={collapsed ? said : entry.hint}>
      <span className="sidebar-link-icon"><Icon name={entry.icon} /></span>
      {!collapsed && <span className="sidebar-link-label">{entry.label}</span>}
      <Count count={entry.count ?? 0} urgent={entry.urgent} collapsed={collapsed} />
      {entry.active && <span className="sidebar-link-indicator" />}
    </Link>
  );
}

/** Each server's decisions, from the queries To do and the app badge already make */
function useInboxMap(servers: Server[]): Map<string, InboxItem[]> {
  const inboxes = useInboxes(servers.map((s) => s.id));
  return new Map(servers.map((s, i) => [s.id, inboxes[i]?.data ?? []]));
}

function ServerSwitcher({ server, servers, inboxes, collapsed, onNavigate }: {
  server: Server; servers: Server[]; inboxes: Map<string, InboxItem[]>; collapsed: boolean; onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); trigger.current?.focus(); } };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  const choose = (id: string) => {
    setOpen(false);
    // On To do, its filter follows; on a server's page, the same page of the other one
    const waiting = params.get('waiting');
    navigate(pathname === '/' ? todoOf(id, waiting && WAITING.some((w) => w.key === waiting) ? waiting as WaitingKind : undefined) : samePageOn(pathname, server.id, id));
    onNavigate?.();
  };
  const initials = nameOf(server).slice(0, 2).toUpperCase();
  return (
    <div ref={box} className="menu-switcher">
      <button ref={trigger} type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="menu-servers"
        aria-label={`Server ${nameOf(server)}, ${server.status}: choose another`} title={collapsed ? nameOf(server) : undefined}
        className="menu-switcher-button">
        <span className="menu-dot" style={{ backgroundColor: toneOf(server.status) }} aria-hidden="true" />
        {collapsed ? <span className="menu-switcher-name">{initials}</span> : (
          <>
            <span className="menu-switcher-name">{nameOf(server)}</span>
            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true"
              style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s ease', flexShrink: 0 }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </>
        )}
      </button>
      {open && (
        <ul id="menu-servers" aria-label="Servers" className="menu-switcher-list">
          {servers.map((s) => {
            const n = inboxes.get(s.id)?.length ?? 0;
            return (
              <li key={s.id}>
                <button type="button" onClick={() => choose(s.id)} aria-current={s.id === server.id ? 'true' : undefined} className={`menu-switcher-option ${s.id === server.id ? 'menu-switcher-option-on' : ''}`}>
                  <span className="menu-dot" style={{ backgroundColor: toneOf(s.status) }} aria-hidden="true" />
                  <span className="menu-switcher-name">{nameOf(s)}</span>
                  {n > 0 && <span className="menu-count" style={{ color: 'var(--color-brand-light)' }} aria-label={`${n} waiting`}>{n}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** A server's pages, those with content: as its tabs were */
function usePages(serverId: string, isAdmin: boolean) {
  const { data: sprint } = useSprint(serverId);
  const { data: mail } = useMail(serverId);
  const { data: meetings } = useMeetings(serverId);
  const { data: reviews } = useReviews(serverId);
  const { data: deployments } = useDeployments(serverId);
  const { data: tickets } = useTickets(serverId);
  const { data: slack } = useSlack(serverId);
  const hasReviews = (reviews?.length ?? 0) > 0 || (deployments?.length ?? 0) > 0 || (tickets?.length ?? 0) > 0;
  return {
    pages: [
      { page: '', label: 'sessions', icon: 'sessions' },
      ...(hasReviews ? [{ page: 'reviews', label: 'reviews', icon: 'reviews' }] : []),
      // People is for admins only: the other users would find an empty page
      ...(isAdmin ? [{ page: 'people', label: 'people', icon: 'people' }] : []),
      ...(sprint?.configured ? [{ page: 'sprint', label: 'sprint', icon: 'sprint' }] : []),
      ...(mail?.enabled ? [{ page: 'mail', label: 'mail', icon: 'mail' }] : []),
      ...(meetings?.connected ? [{ page: 'meetings', label: 'meetings', icon: 'meetings' }] : []),
      { page: 'files', label: 'files', icon: 'files' },
      { page: 'settings', label: 'settings', icon: 'server-settings' },
    ],
    // What the server follows: a source not set up has no shortcut, unless something of it waits all the same
    follows: {
      slack: !!slack?.connection, reviews: (reviews?.length ?? 0) > 0, deploys: (deployments?.length ?? 0) > 0, mail: !!mail?.enabled,
      tickets: !!sprint?.configured || (tickets?.length ?? 0) > 0, sessions: true, other: false,
    } satisfies Record<WaitingKind, boolean>,
  };
}

function ServerSection({ server, servers, inboxes, collapsed, onNavigate }: {
  server: Server; servers: Server[]; inboxes: Map<string, InboxItem[]>; collapsed: boolean; onNavigate?: () => void;
}) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { data: inbox } = useInbox(server.id);
  const { pages, follows } = usePages(server.id, user?.role === 'admin');
  const counts = waitingCounts(inbox ?? []);
  const onTodo = pathname === '/' && params.get('server') === server.id;
  // The one To do shows stays, even emptied
  const waiting: Entry[] = WAITING.filter((w) => follows[w.key] || counts[w.key].count > 0 || (onTodo && params.get('waiting') === w.key)).map((w) => ({
    to: todoOf(server.id, w.key), label: w.label, icon: w.key, hint: w.hint, count: counts[w.key].count, urgent: counts[w.key].urgent,
    active: onTodo && params.get('waiting') === w.key,
  }));
  const base = `/servers/${encodeURIComponent(server.id)}`;
  const page = pathname.startsWith(`${base}/`) ? pathname.slice(base.length + 1).split('/')[0] : pathname === base ? '' : null;
  // A session or a ticket opens under the page that lists it
  const current = page === 'sessions' ? '' : page === 'tickets' ? 'reviews' : page === 'slack' ? null : page;
  return (
    <>
      <ServerSwitcher server={server} servers={servers} inboxes={inboxes} collapsed={collapsed} onNavigate={onNavigate} />
      {!collapsed && <div className="sidebar-section-label">waiting on you</div>}
      <nav className="sidebar-nav sidebar-nav-compact" aria-label={`What waits on you on ${nameOf(server)}`}>
        {waiting.map((e) => <Line key={e.label} entry={e} collapsed={collapsed} onNavigate={onNavigate} />)}
      </nav>
      {!collapsed && <div className="sidebar-section-label">pages</div>}
      <nav className="sidebar-nav sidebar-nav-compact" aria-label={`Pages of ${nameOf(server)}`}>
        {pages.map((p) => (
          <Line key={p.label} collapsed={collapsed} onNavigate={onNavigate}
            entry={{ to: p.page ? `${base}/${p.page}` : base, label: p.label, icon: p.icon, active: current === p.page }} />
        ))}
      </nav>
    </>
  );
}

export function AppMenu({ collapsed = false, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { server, servers } = useCurrentServer();
  const inboxes = useInboxMap(servers);
  const total = [...inboxes.values()].reduce((n, items) => n + items.length, 0);
  const urgent = [...inboxes.values()].some((items) => items.some((i) => i.urgent));
  const global: Entry[] = [
    { to: '/', label: 'to do', icon: 'todo', count: total, urgent, hint: 'Everything that waits on you, on every server', active: pathname === '/' && !params.get('server') },
    { to: '/servers', label: 'servers', icon: 'servers', active: pathname === '/servers' },
    { to: '/schedules', label: 'schedules', icon: 'schedules', active: pathname.startsWith('/schedules') },
    { to: '/costs', label: 'costs', icon: 'costs', active: pathname.startsWith('/costs') },
    { to: '/settings', label: 'settings', icon: 'settings', active: pathname.startsWith('/settings') },
  ];
  return (
    <div className="menu-body">
      {server && <ServerSection server={server} servers={servers} inboxes={inboxes} collapsed={collapsed} onNavigate={onNavigate} />}
      <div className="menu-global">
        {!collapsed && <div className="sidebar-section-label">all servers</div>}
        <nav className="sidebar-nav sidebar-nav-compact" aria-label="All servers">
          {global.map((e) => <Line key={e.label} entry={e} collapsed={collapsed} onNavigate={onNavigate} />)}
        </nav>
      </div>
    </div>
  );
}
